import { UserMenu, useMe } from "@nexxt/account-ui";
import { AppFrame, ViewerProvider } from "@nexxt/app-kit";
import { LogoMark, Sidebar, SidebarItem, SidebarPlaces } from "@nexxt/ui";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  Building2,
  CalendarDays,
  FileText,
  House,
  type LucideIcon,
  MessageSquareWarning,
  Package,
  ShieldCheck,
  Truck,
  UserRound,
} from "lucide-react";
import type { MouseEvent } from "react";
import { m } from "../../paraglide/messages.js";

// The frame of Frontrow (ADR-070 point 6, ADR-216): the frame of Atelier, with the customer's store next to Nexxt so
// that the user recognizes its own space first (brand/README.md §8). Few places, a phone first, those of a domain only
// with the permission to read it: the orders for its buyers (ADR-219), the documents and the shipments of its orders
// (ADR-220), their claims (ADR-228), the booking of its appointments (ADR-237), the organization for its
// administrator (ADR-217); the confirmations come with their step (P8.1h). No search and no shortcuts.

interface Place {
  to:
    | "/"
    | "/orders"
    | "/documents"
    | "/shipments"
    | "/claims"
    | "/appointments"
    | "/organization"
    | "/account"
    | "/security";
  label: () => string;
  icon: LucideIcon;
  permission?: string;
}

const PLACES: Place[] = [
  { to: "/", label: m.nav_home, icon: House },
  { to: "/orders", label: m.nav_orders, icon: Package, permission: "sales_order.read" },
  { to: "/documents", label: m.nav_documents, icon: FileText, permission: "document.read" },
  { to: "/shipments", label: m.nav_shipments, icon: Truck, permission: "fulfillment.read" },
  { to: "/claims", label: m.nav_claims, icon: MessageSquareWarning, permission: "claim.read" },
  { to: "/appointments", label: m.nav_appointments, icon: CalendarDays, permission: "appointment.book" },
  { to: "/organization", label: m.nav_organization, icon: Building2, permission: "organization.read" },
  { to: "/account", label: m.nav_account, icon: UserRound },
  { to: "/security", label: m.nav_security, icon: ShieldCheck },
];

export function FrontrowShell() {
  const { permissions } = useMe();
  return (
    // the screens of @nexxt/party-ui for a user of a customer: never one of Tomorrow
    <ViewerProvider value={{ isInternal: false, permissions }}>
      <AppFrame
        product="frontrow"
        sidebar={({ variant, closeMenu }) => (
          <FrontrowSidebar variant={variant} {...(variant === "sheet" && { onFollowLink: closeMenu })} />
        )}
        topBar={() => (
          <>
            <OrganizationName className="min-w-0 flex-1 truncate border-l border-border pl-2 text-sm" />
            <UserMenu compact />
          </>
        )}
      />
    </ViewerProvider>
  );
}

function OrganizationName({ className }: { className: string }) {
  return <p className={className}>{useMe().organization.name}</p>;
}

function FrontrowSidebar({ variant, onFollowLink }: { variant: "column" | "sheet"; onFollowLink?: () => void }) {
  const me = useMe();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // a session that owes a second factor reaches only the page that adds one (ADR-151)
  const places = me.mfaEnrollmentRequired
    ? PLACES.filter((place) => place.to === "/security")
    : PLACES.filter((place) => !place.permission || me.permissions.includes(place.permission));
  return (
    <Sidebar
      label={m.nav_label()}
      className={variant === "column" ? "w-60 shrink-0 border-r border-border bg-muted px-3 py-3" : "flex-1 p-3"}
      {...(onFollowLink && {
        onClick: (e: MouseEvent) => {
          if (e.target instanceof Element && e.target.closest("a")) onFollowLink();
        },
      })}
    >
      <div className="grid gap-0.5 px-2 py-2">
        <LogoMark product="frontrow" />
        <OrganizationName className="truncate text-sm font-medium" />
      </div>
      <SidebarPlaces className="mt-2 pb-2">
        {places.map((place) => (
          <SidebarItem
            key={place.to}
            render={<Link to={place.to} />}
            icon={<place.icon aria-hidden />}
            label={place.label()}
            active={pathname === place.to || pathname.startsWith(`${place.to}/`)}
          />
        ))}
      </SidebarPlaces>
      <div className="border-t border-border pt-2">
        <UserMenu />
      </div>
    </Sidebar>
  );
}
